import { isStandalone } from './pwaDetection.js';

const KEYS = {
  firstVisitAt: 'tm.pwa.firstVisitAt',
  visitCount: 'tm.pwa.visitCount',
  lastVisitDate: 'tm.pwa.lastVisitDate',
  dismissedAt: 'tm.pwa.dismissedAt',
  dismissCount: 'tm.pwa.dismissCount',
  lastShownAt: 'tm.pwa.lastShownAt',
  installed: 'tm.pwa.installed',
  visitCountAtDismiss: 'tm.pwa.visitCountAtDismiss',
};

const DAY_MS = 24 * 60 * 60 * 1000;

function safeStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readString(key) {
  const s = safeStorage();
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch {
    return null;
  }
}

function writeString(key, value) {
  const s = safeStorage();
  if (!s) return;
  try {
    if (value === null || value === undefined) s.removeItem(key);
    else s.setItem(key, String(value));
  } catch { /* ignore */ }
}

function readNumber(key, fallback = 0) {
  const v = readString(key);
  if (v === null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function readBool(key) {
  return readString(key) === 'true';
}

function todayString(now = Date.now()) {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function recordVisit(now = Date.now()) {
  const today = todayString(now);
  const lastVisitDate = readString(KEYS.lastVisitDate);
  if (!readString(KEYS.firstVisitAt)) {
    writeString(KEYS.firstVisitAt, new Date(now).toISOString());
  }
  if (lastVisitDate !== today) {
    const next = readNumber(KEYS.visitCount, 0) + 1;
    writeString(KEYS.visitCount, next);
    writeString(KEYS.lastVisitDate, today);
    return { incremented: true, visitCount: next };
  }
  return { incremented: false, visitCount: readNumber(KEYS.visitCount, 0) };
}

export function shouldShowBanner(now = Date.now()) {
  if (readBool(KEYS.installed)) {
    return { show: false, reason: 'already_installed' };
  }
  if (isStandalone()) {
    return { show: false, reason: 'in_standalone' };
  }
  const lastShownAt = readString(KEYS.lastShownAt);
  if (lastShownAt && todayString(Date.parse(lastShownAt)) === todayString(now)) {
    return { show: false, reason: 'shown_today' };
  }
  const visitCount = readNumber(KEYS.visitCount, 0);
  const dismissedAt = readString(KEYS.dismissedAt);
  if (dismissedAt) {
    const dismissedTs = Date.parse(dismissedAt);
    const daysSinceDismiss = (now - dismissedTs) / DAY_MS;
    const visitsSinceDismiss =
      visitCount - readNumber(KEYS.visitCountAtDismiss, 0);
    if (daysSinceDismiss >= 2 || visitsSinceDismiss >= 10) {
      return { show: true, reason: '2d_or_10visits' };
    }
    return { show: false, reason: 'cooldown_active' };
  }
  if (visitCount <= 1) {
    return { show: true, reason: 'first_visit' };
  }
  return { show: true, reason: 'returning_no_dismiss' };
}

export function markShown(now = Date.now()) {
  writeString(KEYS.lastShownAt, new Date(now).toISOString());
}

export function markDismissed(now = Date.now()) {
  writeString(KEYS.dismissedAt, new Date(now).toISOString());
  const next = readNumber(KEYS.dismissCount, 0) + 1;
  writeString(KEYS.dismissCount, next);
  writeString(KEYS.visitCountAtDismiss, readNumber(KEYS.visitCount, 0));
}

export function markInstalled() {
  writeString(KEYS.installed, 'true');
}

export function getDebugSnapshot(now = Date.now()) {
  const firstVisitAt = readString(KEYS.firstVisitAt);
  const daysSinceFirstVisit = firstVisitAt
    ? Math.max(0, Math.floor((now - Date.parse(firstVisitAt)) / DAY_MS))
    : 0;
  return {
    firstVisitAt,
    visitCount: readNumber(KEYS.visitCount, 0),
    lastVisitDate: readString(KEYS.lastVisitDate),
    dismissedAt: readString(KEYS.dismissedAt),
    dismissCount: readNumber(KEYS.dismissCount, 0),
    lastShownAt: readString(KEYS.lastShownAt),
    installed: readBool(KEYS.installed),
    visitCountAtDismiss: readNumber(KEYS.visitCountAtDismiss, 0),
    daysSinceFirstVisit,
  };
}

export const _internal = { KEYS, todayString };
